/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

// material-ui
import makeStyles from "@material-ui/core/styles/makeStyles";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import Dialog from "@material-ui/core/Dialog";

// icons
import HistoryIcon from "@material-ui/icons/History";
import DeleteIcon from "@material-ui/icons/Delete";

// jaza-soft
import {
  PageHeader,
  SimpleForm,
  translate,
  SaveButton,
  BackButton,
  EditButton,
  DeleteButton,
  Button,
  PageFooter,
  required,
  showNotification
} from "jazasoft";

// local
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";
import Forbidden from "../../../components/Forbidden";
import MyFileInput from "../../../components/MyFileInput";

import missingImage from "../../../asset/img/image_not_available.png";

// helper
import { FlowInfoFields } from "../../../utils/entityFields";
import hasPrivilege from "../../../utils/hasPrivilege";

import { getDistinctValues, isEmpty, isTrue } from "../../../utils/helpers";
import { format } from "../../../utils/dateUtils";
import { ResourceId, Scope } from "../../../utils/privilege";
import { colorSecondary, colorTomatoRed } from "../../../utils/colors";
import compressImage from "../../../utils/compressImage";
import { useDeepEffect, useDeepMemo, useEntityPrivilege, useMutation, useQuery, useSafeState, useSetting, useTime, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import PartSizeDetails from "./PartSizeDetails";
import { OState } from "../../../utils/types";
import { appUrl } from "../../../App";
import StatePopover from "./StatePopover";

const Preview = ({ visible, filename, flowInfo, order, onClose, updateFlowInfo }) => {
  const onDeleteImage = () => {
    updateFlowInfo && updateFlowInfo(flowInfo);
  };

  const onSubmit = async ({ file }) => {
    const rawFile = file.rawFile;
    const size = Math.round((rawFile?.size || 0) / (1024 * 1024));
    const quality = size > 5 ? 0.2 : size > 3 ? 0.3 : 0.4;
    const compressedFile = await compressImage(rawFile, quality);
    updateFlowInfo && updateFlowInfo("image", flowInfo, compressedFile);
  };

  return (
    <Dialog open={visible || false} onClose={onClose} fullWidth maxWidth={"md"}>
      {!isEmpty(filename) && (
        <div style={{ display: "flex", justifyContent: "center", padding: "0.75em" }}>
          <Button label="Delete" variant="outlined" style={{ color: colorTomatoRed.dark }} onClick={onDeleteImage}>
            <DeleteIcon />
          </Button>
        </div>
      )}
      {!isEmpty(filename) && <img src={`${appUrl}/files/${filename}?path=order/${order?.id}/${flowInfo?.id}`} alt={filename} height="60%" />}
      {isEmpty(filename) && (
        <div>
          <CardHeader title="Upload Style Image" />
          <Divider />
          <div style={{ padding: "1.5em" }}>
            <SimpleForm
              onSubmit={onSubmit}
              footer={props => (
                <PageFooter>
                  <SaveButton {...props} />
                  <Button variant="outlined" color="primary" label="Cancel" onClick={onClose} />
                </PageFooter>
              )}>
              <MyFileInput
                source="file"
                label="Style Image File"
                accept={{
                  "image/jpeg": [".jpeg"],
                  "image/png": [".png"],
                  "image/git": [".git"]
                }}
                message={"Drag and drop file here, or click to select file"}
                xs={12}
                fullWidth
                validate={required()}
              />
            </SimpleForm>
          </div>
        </div>
      )}
    </Dialog>
  );
};

const renderStyleImage = (value, order, flowInfo, onClick) => {
  const imgUrl = isEmpty(value) ? missingImage : `${appUrl}/files/${value}?path=order/${order?.id}/${flowInfo?.id}`;
  return (
    <div onClick={() => onClick && onClick(value, flowInfo)}>
      <img src={imgUrl} alt={value} style={{ height: 40, backgroundColor: isEmpty(value) ? "yellow" : "none" }} />
    </div>
  );
};

const renderFlowInfoStatus = (flowInfo, onClick) => {
  const stateList = JSON.parse(flowInfo.state);
  if (stateList?.length === 1) {
    return <span>{stateList[0]?.state}</span>;
  } else if (stateList?.length > 1) {
    const states = stateList.map(e => e.state);

    const effState = getDistinctValues(
      Object.values(OState)
        .filter(e => states.includes(e.state))
        .sort((a, b) => a.stateValue - b.stateValue)
        .map(e => e.state)
    ).join(", \n");
    return (
      <div style={{ color: colorSecondary.dark, whiteSpace: "pre-line", width: 200 }} onClick={event => onClick(event, stateList)}>
        {effState}*
      </div>
    );
  }
  return "";
};

const useStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(3),
    marginTop: 0
  },
  card: {
    padding: 0,
    boxShadow: theme.shadows[1],
    backgroundColor: "white",
    borderRadius: 4
  },
  mrTop: {
    marginTop: "1.5em"
  },
  padding: {
    padding: "1.5em"
  },
  footer: {
    marginLeft: 24,
    "& > *": {
      marginRight: "1em"
    }
  }
}));

const OrderView = props => {
  const classes = useStyles();
  const { id, roles, translate, history, hasAccess, basePath } = props;
  const resource = "orders";

  const [version, setVersion] = useSafeState(0);
  const [selectedKeys, setSelectedKeys] = useSafeState([]);
  const [dialogState, setDialogState] = useSafeState({
    active: false,
    name: null,
    orderFlow: null,
    filename: null,
    flowInfo: null
  });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [stateList, setStateList] = React.useState([]);
  const { allUnitList } = useUnit();

  const { data: order = {} } = useQuery(id && { url: `orders/${id}`, params: { view: "deep" }, version });
  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");
  const flowInfoValidation = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, "flowInfo");

  const dispatch = useDispatch();
  const { utc_offset } = useTime();
  const [mutate] = useMutation();
  const { disableEdit, disableDelete } = useEntityPrivilege("order");

  const updateFlowInfo = (action, { exFactoryDate, ...flowInfo }, styleImageFile, onActionComplete) => {
    if (isEmpty(flowInfo)) return;

    if (action === "image") {
      const formData = new FormData();

      if (styleImageFile) {
        formData.append("body", JSON.stringify(flowInfo));
        formData.append("styleImage", styleImageFile);
      } else {
        formData.append("body", JSON.stringify({ ...flowInfo, styleImage: null }));
      }

      let options = {
        url: `orders/${id}/flowInfo/${flowInfo?.id}`,
        method: "patch",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" }
      };

      mutate(options, {
        onSuccess: () => {
          setVersion(version + 1);
          setDialogState({});
          if (styleImageFile) {
            dispatch(showNotification("Style Image updated successfully."));
          } else {
            dispatch(showNotification("Style Image deleted successfully."));
          }
        }
      });
    } else {
      let options = {
        url: `orders/${id}/flowInfo/${flowInfo?.id}`,
        method: "put",
        data: flowInfo,
        params: { action }
      };

      mutate(options, {
        onSuccess: () => {
          setVersion(version + 1);
          if (action === "part") {
            dispatch(showNotification("Part details updated successfully."));
          } else {
            dispatch(showNotification("Size Breakup updated successfully."));
          }
          onActionComplete && onActionComplete();
        }
      });
    }
  };

  const onStateClick = (event, stateList) => {
    setStateList(stateList || []);
    setAnchorEl(event.currentTarget);
  };
  const poInfoRows = useDeepMemo(() => {
    if (isEmpty(order)) return [];
    return [{ ...order, customerName: order?.customer?.name, seasonName: order?.season?.name }];
  }, [order]);

  const poInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerPoRef", title: "Customer PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "merchant", title: "Merchant" },
      { dataKey: "customField1", title: "Custom Field 1" },
      { dataKey: "customField2", title: "Custom Field 2" },
      { dataKey: "customField3", title: "Custom Field 3" },
      { dataKey: "desc", title: "Remarks" }
    ];
    cols = cols.filter(c => orderFields.includes(c.dataKey));
    return cols;
  }, [orderFields]);

  const rows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    const rowList = order.flowInfoList
      .filter(fi => isEmpty(fi.orderParentId))
      .map(fi => ({
        ...fi,
        key: fi.id,
        parentKey: fi.parentId,
        productName: fi.product?.name,
        exFactoryDate: utc_offset && !isEmpty(fi.exFactoryDate) ? format(fi.exFactoryDate, utc_offset, "dd MMM, yy") : null
      }))
      .sort((a, b) => a.serialNo - b.serialNo);

    return rowList;
  }, [order?.flowInfoList, utc_offset]);

  // Columns and rows for Order flow table
  const columns = useDeepMemo(() => {
    if (isEmpty(flowInfoFields) || isEmpty(flowInfoValidation)) return [];
    const onClick = (filename, flowInfo) => {
      setDialogState({ active: true, name: "styleImage", filename, flowInfo });
    };

    let columns = [
      { dataKey: FlowInfoFields.ID, title: "ID" },
      {
        dataKey: FlowInfoFields.STYLE_IMAGE,
        title: "Style Image",
        length: 500,
        render: (value, _, row) => renderStyleImage(value, order, row, onClick),
        headerCellProps: { style: { textAlign: "center" } },
        rowCellProps: { style: { paddingTop: 4, paddingBottom: 0, width: 100, textAlign: "center" } }
      },
      {
        dataKey: FlowInfoFields.PRODUCT_NAME,
        title: "Product",
        filterOptions: { filter: true },
        highlight: true
      },
      { dataKey: FlowInfoFields.RATIO, title: "Ratio", highlight: true },
      { dataKey: FlowInfoFields.STYLE_NO, title: "Style No", filterOptions: { filter: true }, render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.STYLE, title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: FlowInfoFields.COLOR, title: "Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: FlowInfoFields.FIT, title: "Fit", filterOptions: { filter: true }, render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.INSEAM, title: "Inseam", filterOptions: { filter: true }, render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.DESTINATION, title: "Destination", filterOptions: { filter: true }, render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.DEL_MODE, title: "Delivery Mode", render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.GENDER, title: "Gender", render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.SLEEVE_TYPE, title: "Sleeve Type", render: value => value || "-", highlight: true },
      {
        dataKey: FlowInfoFields.EX_FACTORY_DATE,
        title: "Ex-Factory Date",
        filterOptions: { filter: true },
        render: value => value || "-",
        highlight: true
      },
      {
        dataKey: FlowInfoFields.ORDER_QTY,
        title: "Order Qty",
        render: (value, _, row) => `${value}${isTrue(row.product?.isSet) ? " Set" : " Pc"}`
      },
      { dataKey: FlowInfoFields.EXTRA, title: "Extra", render: value => (isEmpty(value) ? "-" : `${value}%`) },
      { dataKey: FlowInfoFields.SHORT_SHIP, title: "Short Ship (%)", render: value => (isEmpty(value) ? "-" : `${value}%`) },
      { dataKey: FlowInfoFields.FLOW_REF, title: "Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.DEL_FLOW_REF, title: "Del. Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.CUSTOMER_FLOW_REF, title: "Customer Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.CUSTOM_FIELD_1, title: "CustomField 1", render: value => value || "-" },
      { dataKey: FlowInfoFields.CUSTOM_FIELD_2, title: "CustomField 2", render: value => value || "-" },
      { dataKey: FlowInfoFields.CUSTOM_FIELD_3, title: "CustomField 3", render: value => value || "-" },
      {
        dataKey: FlowInfoFields.STATUS,
        title: "Status",
        length: 1000,
        linkPath: () => {},
        render: (value, _, row) => renderFlowInfoStatus(row, onStateClick)
      }
    ];

    if (rows.every(e => isEmpty(e.ratio))) {
      columns = columns.filter(c => c.dataKey !== FlowInfoFields.RATIO);
    }

    columns = columns.filter(c => c.dataKey === FlowInfoFields.STATE || flowInfoFields.includes(c.dataKey));
    return columns;
  }, [flowInfoFields, flowInfoValidation, rows]);

  useDeepEffect(() => {
    if (isEmpty(rows)) return;
    setSelectedKeys([rows.filter(e => isEmpty(e.parentKey))[0]?.key]);
  }, [rows?.length]);

  const onRowClick = row => {
    setSelectedKeys([row.id]);
  };

  const parentIds = getDistinctValues(
    order?.flowInfoList
      ?.filter(fi => !isEmpty(fi.parentId))
      .filter(fi => isEmpty(order?.flowInfoList?.find(f => f.id === fi.parentId)?.parentId))
      .map(fi => fi.parentId)
  );
  const defaultExpanded = parentIds?.length <= 1;

  const flowInfo = selectedKeys?.length === 1 ? order?.flowInfoList?.find(fi => selectedKeys[0] === fi.id) : null;

  if (!hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.READ)) {
    return <Forbidden history={history} />;
  }

  return (
    <div>
      <PageHeader title="resources.orders.pageHeader.view" id={id} titleArgs={{ _: `Order #${id}` }} />

      <StatePopover anchorEl={anchorEl} setAnchorEl={setAnchorEl} stateList={stateList} unitList={allUnitList} />

      <Preview
        visible={dialogState.active && dialogState.name === "styleImage"}
        filename={dialogState.filename}
        flowInfo={dialogState.flowInfo}
        order={order}
        updateFlowInfo={updateFlowInfo}
        onClose={() => setDialogState({ active: false })}
      />

      <div className={classes.container}>
        {/* Order Summary */}
        <MuiTable
          title={<CardHeader style={{ padding: 0 }} title={translate(`resources.orders.cardHeader.summary`, { _: "Order Summary" })} />}
          toolbar
          columns={poInfoColumns}
          rows={poInfoRows}
          i18nKey="resources.orders.fields"
        />

        {/* Order Details */}
        <Card className={classes.mrTop}>
          <PerfectScrollbar>
            <MuiTable
              title={<CardHeader style={{ padding: 0 }} title={translate(`resources.orders.cardHeader.orderDetails`, { _: `Order Details` })} />}
              columns={columns}
              rows={rows}
              searchable={true}
              pageable={true}
              searchKeys={["flowRef", "delFlowRef", "customerFlowRef", "style", "styleNo"]}
              idKey="key"
              parentIdKey="parentKey"
              defaultExpanded={(row, level) => defaultExpanded && level === 0}
              onRowClick={onRowClick}
              rowStyle={({ row, rowIdx }) => (selectedKeys?.includes(row?.key) ? { backgroundColor: "#BFDDF8" } : {})}
              i18nKey="resources.orders.fields.flowInfo"
            />
          </PerfectScrollbar>
        </Card>
        <PartSizeDetails
          className={classes.mrTop}
          order={order}
          flowInfo={flowInfo}
          onRefresh={() => setVersion(v => v + 1)}
          roles={roles}
          hasAccess={hasAccess}
          translate={translate}
          disableEdit={disableEdit}
        />
      </div>

      <PageFooter className={classes.footer}>
        <BackButton variant="outlined" color="primary" />
        {hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.UPDATE) && !disableEdit && (
          <EditButton variant="outlined" color="primary" basePath={basePath} resource={resource} to="edit" />
        )}
        <Button
          label={translate(`js.action.history`, { _: `History` })}
          component={Link}
          to={`/orders/${id}/history`}
          variant="outlined"
          color="primary">
          <HistoryIcon />
        </Button>
        {hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.DELETE) && !disableDelete && (
          <DeleteButton
            variant="outlined"
            resource={resource}
            basePath={basePath}
            record={order}
            recordName={translate(`resource.orders.name`, { _: "Order" })}
          />
        )}
      </PageFooter>
    </div>
  );
};

export default translate(OrderView);
